import React, { useState, useEffect, useMemo } from 'react';

import { AdminPage, Button, ControlMenu, ErrorMessage } from 'components';
import { H3 } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { useOrganizationSettingsData } from 'lane-shared/domains/visitorManagement/hooks/useOrganizationSettingsData';
import {
  ActivateEmailTemplate,
  EmailTemplateType,
} from 'lane-shared/domains/visitorManagement/types';
import { VisitorConfiguration } from '../../types/VisitorConfiguration';
import { OrganizationSettings } from 'lane-shared/domains/visitorManagement/types/OrganizationSettingsType';
import {
  getVisitorEmailTemplate,
  upsertVisitorEmailTemplate,
  upsertVisitorManagementOrganizationSettings,
} from 'lane-shared/graphql/visitorManagement';
import { upsertVisitorWatchlistLevelConfigs } from 'graphql-queries';
import { castForUpdate, hasPermission } from 'lane-shared/helpers';
import { pause } from 'async-timing';
import { convertToUUID } from 'uuid-encoding';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { UserType } from 'lane-shared/types/User';
import { PERMISSION_KEYS } from 'constants-permissions';

import TabStrip from 'components/general/TabStrip';
import { useQueryString } from 'hooks';

import history from '../../../../helpers/history';
import LeaveConfirmationPrompt from '../../components/LeaveConfirmationPrompt/LeaveConfirmationPrompt';
import { VisitorManagementEmailTemplateTab } from './tabs/emailTemplate';
import { VisitorManagementGeneralTab } from './tabs/general';
import { VisitorManagementNotificationsTab } from './tabs/notifications';
import { VisitorManagementSchedulingTab } from './tabs/scheduling';

import styles from './styles.scss';
import { WatchlistLevelsTab } from './tabs/watchlistLevels';

function safeHasPermission(...args: Parameters<typeof hasPermission>): boolean {
  try {
    return hasPermission(...args);
  } catch {
    return false;
  }
}

const defaultTab = 'notifications';

type TabConfig = {
  label: string;
  value: string;
  Component: ({
    channel,
    config,
    onConfigUpdated,
    loading,
    showValidationErrors,
  }: {
    channel: any;
    config: VisitorConfiguration;
    onConfigUpdated: (update: Partial<VisitorConfiguration>) => void;
    loading?: boolean;
    showValidationErrors?: boolean;
  }) => JSX.Element;
};

type TabsMapType = Record<string, TabConfig>;

const ALL_TABS: TabsMapType = {
  general: {
    label: 'web.admin.visitorManagement.configuration.tab.general',
    value: 'general',
    Component: VisitorManagementGeneralTab,
  },
  scheduling: {
    label: 'web.admin.visitorManagement.configuration.tab.scheduling',
    value: 'scheduling',
    Component: VisitorManagementSchedulingTab,
  },
  notifications: {
    label: 'web.admin.visitorManagement.configuration.tab.notifications',
    value: 'notifications',
    Component: VisitorManagementNotificationsTab,
  },
  emailTemplate: {
    label: 'web.admin.visitorManagement.configuration.tab.emailTemplate',
    value: 'emailTemplate',
    Component: VisitorManagementEmailTemplateTab,
  },
  watchlistLevels: {
    label: 'web.admin.visitorManagement.configuration.tab.watchlistLevels',
    value: 'watchlistLevels',
    Component: WatchlistLevelsTab,
  },
};

const getTabComponent = (
  selectedTab: string,
  tabsMap: TabsMapType
): TabConfig['Component'] => {
  return (
    tabsMap[selectedTab]?.Component ??
    tabsMap[defaultTab]?.Component ??
    Object.values(tabsMap)[0]?.Component ??
    (() => {
      throw new Error(`No tabs available in tabsMap`);
    })()
  );
};

type VisitorConfigurationControlMenuProps = {
  query: { tab: string };
  tabsMap: TabsMapType;
  updatedConfig: VisitorConfiguration | null;
  disableButtons: boolean;
  resetData: () => void;
  loading: boolean;
  onSave: () => Promise<void>;
  goToUrl: (props: any) => void;
  t: (key: string) => string;
};

const VisitorConfigurationControlMenu = ({
  query,
  tabsMap,
  updatedConfig,
  disableButtons,
  resetData,
  loading,
  onSave,
  goToUrl,
  t,
}: VisitorConfigurationControlMenuProps) => {
  return (
    <ControlMenu>
      <TabStrip
        tabs={Object.values(tabsMap)}
        selected={{ value: query.tab }}
        onSelectTab={tab => goToUrl({ tab: tab.value })}
        className={styles.tabs}
      />
      <Button
        testId="resetButton"
        disabled={!updatedConfig || disableButtons}
        onClick={resetData}
        loading={loading}
      >
        {t('web.admin.channel.visitor.log.configuration.tabs.button.undo')}
      </Button>
      <Button
        testId="saveButton"
        disabled={!updatedConfig || disableButtons}
        onClick={onSave}
        loading={loading}
        variant="contained"
      >
        {t('web.admin.channel.visitor.log.configuration.tabs.button.save')}
      </Button>
    </ControlMenu>
  );
};

export function VisitorConfigurationPage({
  channel,
  user,
}: {
  channel: any;
  user: UserType;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const { data: emailTemplateData, refetch: refetchEmailTemplate } = useQuery(
    getVisitorEmailTemplate,
    {
      variables: {
        channelId: convertToUUID(channel?._id),
        templateType: EmailTemplateType.INVITE,
      },
      fetchPolicy: 'network-only',
    }
  );

  const isGeneralSettingsEnabled = useFlag(
    FeatureFlag.VisitorManagementGeneralSettings,
    false
  );
  const isSchedulingSettingsEnabled = useFlag(
    FeatureFlag.VisitorManagementSchedulingSettings,
    false
  );
  const isWatchlistV2Enabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistV2,
    false
  );
  const isWatchlistLevelsTabAdminsRequireWatchlistConfigureEnabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistLevelsTabAdminsRequireWatchlistConfigure,
    false
  );

  // Check if user is admin
  const isAdmin =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel?._id
    );
  // Legacy: super user + implied admin via PERMISSION_ADMIN count as having watchlist configure.
  const hasConfigureWatchlistPermissionLegacy =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_CONFIGURE],
      channel?._id
    );

  // Strict: only explicit PERMISSION_VISITORS_WATCHLIST_CONFIGURE on the role (for Watchlist levels tab).
  // Only evaluated when the flag is on; defaults to false when the flag is off.
  const hasConfigureWatchlistPermissionStrict =
    isWatchlistLevelsTabAdminsRequireWatchlistConfigureEnabled &&
    safeHasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_CONFIGURE],
      channel?._id,
      false
    );

  const hasConfigureWatchlistPermissionForTabs =
    isWatchlistLevelsTabAdminsRequireWatchlistConfigureEnabled
      ? hasConfigureWatchlistPermissionStrict
      : hasConfigureWatchlistPermissionLegacy;

  // Build filtered tabs map based on feature flags and permissions
  // Tab order: general -> notifications -> email template -> watchlist levels
  const tabsMap = useMemo((): TabsMapType => {
    // If user has configure permission but is not admin, show only watchlistLevels tab
    if (
      hasConfigureWatchlistPermissionForTabs &&
      !isAdmin &&
      isWatchlistV2Enabled
    ) {
      return { watchlistLevels: ALL_TABS.watchlistLevels };
    }

    // Admins and others: tabs filtered by feature flags.
    const tabs: TabsMapType = {};

    if (isGeneralSettingsEnabled) {
      tabs.general = ALL_TABS.general;
    }

    tabs.notifications = ALL_TABS.notifications;
    tabs.emailTemplate = ALL_TABS.emailTemplate;

    if (
      isWatchlistV2Enabled &&
      (!isWatchlistLevelsTabAdminsRequireWatchlistConfigureEnabled ||
        hasConfigureWatchlistPermissionStrict)
    ) {
      tabs.watchlistLevels = ALL_TABS.watchlistLevels;
    }

    return tabs;
  }, [
    isGeneralSettingsEnabled,
    isSchedulingSettingsEnabled,
    isWatchlistV2Enabled,
    isWatchlistLevelsTabAdminsRequireWatchlistConfigureEnabled,
    hasConfigureWatchlistPermissionForTabs,
    hasConfigureWatchlistPermissionStrict,
    isAdmin,
  ]);

  const {
    organizationSettings: organizationSettingsData,
    refetch: refetchOrganizationSettings,
    loading: organizationSettingsLoading,
  } = useOrganizationSettingsData(convertToUUID(channel?._id));

  if (loading !== organizationSettingsLoading) {
    setLoading(organizationSettingsLoading);
  }

  const { t } = useTranslation();

  // Determine default tab - always notifications
  const availableTabKeys = Object.keys(tabsMap);
  const defaultTabForUser = useMemo(() => {
    // Always default to notifications
    if (tabsMap[defaultTab]) {
      return defaultTab;
    }

    return availableTabKeys[0] ?? defaultTab;
  }, [tabsMap, availableTabKeys]);

  const [query, goToUrl] = useQueryString<{
    tab: string;
  }>({
    tab: defaultTabForUser,
  });

  // Ensure query.tab exists in tabsMap, reset if not
  useEffect(() => {
    if (!tabsMap[query.tab]) {
      goToUrl({ tab: defaultTabForUser });
    }
  }, [query.tab, tabsMap, defaultTabForUser, goToUrl]);

  const [error, setError] = useState(null);
  const [updatedConfig, setUpdatedConfig] =
    useState<VisitorConfiguration | null>(null);
  const [updatedEmailTemplateProperties, setUpdatedEmailTemplateProperties] =
    useState<string[]>([]);

  const [
    updatedOrganizationSettingsProperties,
    setUpdatedOrganizationSettingsProperties,
  ] = useState<string[]>([]);

  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const visitorConfiguration: VisitorConfiguration = {
    emailTemplate: {
      ...emailTemplateData?.visitorEmailTemplate,
      ...updatedConfig?.emailTemplate,
    },
    organizationSettings: {
      ...organizationSettingsData,
      ...updatedConfig?.organizationSettings,
    },
    watchlistLevels: updatedConfig?.watchlistLevels,
  };

  // Validates watchlist levels configuration
  const hasWatchlistValidationErrors = (): boolean => {
    const watchlistLevels = updatedConfig?.watchlistLevels;

    if (!watchlistLevels) {
      return false;
    }

    return watchlistLevels.some(
      level =>
        level.isNotificationsEnabled && level.notificationTargets.length === 0
    );
  };

  const onConfigUpdated = (update: Partial<VisitorConfiguration>) => {
    setDisableButtons(false);
    setShowValidationErrors(false);
    setUpdatedConfig({
      emailTemplate: {
        ...(updatedConfig?.emailTemplate ||
          emailTemplateData?.visitorEmailTemplate),
        ...update?.emailTemplate,
      },

      organizationSettings: {
        ...updatedConfig?.organizationSettings,
        ...update?.organizationSettings,
      },

      watchlistLevels:
        update?.watchlistLevels ?? updatedConfig?.watchlistLevels,
    });

    if (update.emailTemplate) {
      setUpdatedEmailTemplateProperties([
        'qrEnabled',
        ...updatedEmailTemplateProperties,
        ...Object.keys(update.emailTemplate).filter(
          key => !updatedEmailTemplateProperties.includes(key)
        ),
      ]);
    }

    if (update.organizationSettings) {
      setUpdatedOrganizationSettingsProperties([
        ...updatedOrganizationSettingsProperties,
        ...Object.keys(update.organizationSettings).filter(
          key => !updatedOrganizationSettingsProperties.includes(key)
        ),
      ]);
    }
  };

  const resetData = () => {
    setUpdatedEmailTemplateProperties([]);
    setUpdatedOrganizationSettingsProperties([]);
    setUpdatedConfig(null);
    setShowValidationErrors(false);
    refetchEmailTemplate();
    refetchOrganizationSettings();
    // TODO: Add refetch for watchlist levels when query is implemented
  };

  const onSave = async () => {
    // Check for validation errors before saving
    if (hasWatchlistValidationErrors()) {
      setShowValidationErrors(true);

      // Navigate to watchlistLevels tab if user is on a different tab
      if (query.tab !== 'watchlistLevels') {
        goToUrl({ tab: 'watchlistLevels' });
      }

      // Show a toast message to inform the user about the validation error
      window.Toast.show(
        t(
          'web.admin.visitorManagement.configuration.watchlistLevels.validation.error'
        )
      );

      return;
    }

    setDisableButtons(true);

    try {
      setLoading(true);
      setError(null);

      if (updatedConfig?.emailTemplate) {
        await saveEmailTemplate(updatedConfig?.emailTemplate);
      }

      if (updatedConfig?.organizationSettings) {
        await saveOrganizationSettings(updatedConfig?.organizationSettings);
      }

      if (updatedConfig?.watchlistLevels) {
        await saveWatchlistLevels(updatedConfig?.watchlistLevels);
      }

      await pause();

      await window.Toast.show(
        t('web.admin.visitorManagement.configuration.save.success')
      );
    } catch (err: any) {
      setError(err);
      window.Alert.alert({
        title: t('web.admin.visitorManagement.configuration.save.error.title'),
        message: t(
          'web.admin.visitorManagement.configuration.save.error.message'
        ),
        error: err,
      });
    }
  };

  async function saveEmailTemplate(
    updatedEmailTemplate: ActivateEmailTemplate
  ) {
    const update: Partial<ActivateEmailTemplate> = {};

    updatedEmailTemplateProperties.forEach((key: string) => {
      const keyTyped = key as keyof ActivateEmailTemplate;

      // @ts-ignore Type 'string | boolean | Date | undefined' is not assignable to type 'undefined'.
      update[keyTyped] = updatedEmailTemplate[keyTyped];
    });

    if (Object.keys(update).length !== 0) {
      const casted = castForUpdate(update);

      await getClient().mutate({
        mutation: upsertVisitorEmailTemplate,
        variables: {
          emailTemplate: {
            ...casted,
          },
        },
      });
    }
  }

  async function saveOrganizationSettings(
    updatedOrganizationSettings: OrganizationSettings
  ) {
    const update: OrganizationSettings = {};

    updatedOrganizationSettingsProperties.forEach((key: string) => {
      const keyTyped = key as keyof OrganizationSettings;

      // @ts-ignore Type 'string | boolean | Date | undefined' is not assignable to type 'undefined'.
      update[keyTyped] = updatedOrganizationSettings[keyTyped];
    });

    if (Object.keys(update).length !== 0) {
      const casted = castForUpdate(update);

      delete casted.createdAtDatetime;
      delete casted.updatedAtDatetime;
      delete casted.createdByUserId;
      delete casted.updatedByUserId;

      await getClient().mutate({
        mutation: upsertVisitorManagementOrganizationSettings,
        variables: {
          organizationSettings: {
            ...casted,
          },
        },
      });
    }
  }

  async function saveWatchlistLevels(
    updatedWatchlistLevels: VisitorConfiguration['watchlistLevels']
  ) {
    if (!updatedWatchlistLevels || updatedWatchlistLevels.length === 0) {
      return;
    }

    // Map WatchlistLevelConfig to WatchlistLevelConfigInput format
    const watchlistLevelConfigs = updatedWatchlistLevels.map(config => ({
      organizationId: config.organizationId,
      level: config.level,
      displayLevelText: config.displayLevelText,
      isNotificationsEnabled: config.isNotificationsEnabled,
      notificationTargets: config.notificationTargets.map(convertToUUID),
      isWatchlistDisplayed: config.isWatchlistDisplayed,
      isApprovalRequired: config.isApprovalRequired,
    }));

    await getClient().mutate({
      mutation: upsertVisitorWatchlistLevelConfigs,
      variables: {
        input: {
          organizationId: channel._id,
          watchlistLevelConfigs,
        },
      },
    });
  }

  const CurrentTab = getTabComponent(query.tab, tabsMap);

  return (
    <>
      <LeaveConfirmationPrompt
        testId="visitorLeaveConfirmation"
        when={!!updatedConfig && !disableButtons}
        navigate={path => history.push(path)}
        title={t('web.admin.visitorManagement.configuration.leavePrompt.title')}
        description={t(
          'web.admin.visitorManagement.configuration.leavePrompt.description'
        )}
      />
      <AdminPage>
        <H3 as="h1">{t('web.admin.visitorManagement.configuration')}</H3>
        <VisitorConfigurationControlMenu
          tabsMap={tabsMap}
          updatedConfig={updatedConfig}
          disableButtons={disableButtons}
          loading={loading}
          query={query}
          resetData={resetData}
          onSave={onSave}
          goToUrl={goToUrl}
          t={t}
        />
        <ErrorMessage error={error} />
        <CurrentTab
          channel={channel}
          config={visitorConfiguration}
          onConfigUpdated={onConfigUpdated}
          loading={loading}
          showValidationErrors={showValidationErrors}
        />
      </AdminPage>
    </>
  );
}
